/**
 * The front door's photos fill their frames like CSS `object-fit: cover`. Bash's speech bubble and
 * the "Your seat" tag point at spots IN the photo, so they are placed with the same crop the
 * browser applies (vestra-v3 pages/waitlist.html `place()`, ported).
 */

export interface CoverBox {
  /** The photo's offset inside its frame (the porcelain edge), px. */
  inset: number;
  /** The photo's on-screen box, px. */
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
  /** object-position as fractions, e.g. [0.5, 0.3] for "50% 30%". */
  position: [number, number];
}

/** Where a point of the photo (fractions of its natural size) lands in the frame, px. */
export function coverPoint(
  box: CoverBox,
  [x, y]: [number, number]
): [number, number] {
  const k = Math.max(
    box.width / box.naturalWidth,
    box.height / box.naturalHeight
  );
  const dw = box.naturalWidth * k;
  const dh = box.naturalHeight * k;
  return [
    box.inset + (box.width - dw) * box.position[0] + x * dw,
    box.inset + (box.height - dh) * box.position[1] + y * dh,
  ];
}

/**
 * The bubble's left/top (its top edge sits ABOVE `top`: the bubble is translated up by its own
 * height). Just over the head, a little before it, and always 12px inside the photo's edges.
 */
export function bubbleAt(
  box: CoverBox,
  head: [number, number],
  bubble: { width: number; height: number }
): [number, number] {
  const [bx, by] = coverPoint(box, head);
  const left = Math.min(
    Math.max(box.inset + 12, bx - 24),
    box.inset + box.width - bubble.width - 12
  );
  const top = Math.max(box.inset + 12 + bubble.height, by - 8);
  return [Math.round(left), Math.round(top)];
}
